<script lang="ts">
  import {
    onDestroy,
    onMount,
    setContext,
    SvelteComponent,
  } from "svelte"
  import type {
    FigureName as FigureNameType,
    GameType,
    RealTimeEvent,
  } from "../../global"
  import type { Board } from "../../Model/Board"
  import type { Game } from "../../Model/Game"
  import {
    boardsService,
    hasBoard,
    playerService,
    playersFactory,
    room,
    roomsService,
  } from "../../Services"
  import type { EventsChannel } from "../../Services/EventsChannel"
  import BoardBingo90 from "../Board/BoardBingo90.svelte"
  import BoardBingo75 from "../Board/BoardBingo75.svelte"
  import GameHistoric from "../Game/GameHistoric.svelte"
  import If from "../Shared/If.svelte"
  import BoardBingoMex from "../Board/BoardBingoMex.svelte"
  import GetBoardForm from "../Board/GetBoardForm.svelte"
  import type { Player } from "../../Model/Player"
  import AwardScreen from "../Game/AwardScreen.svelte"
  import { useLocation } from "svelte-navigator"
  import MessagesMarquee from "../Messages/MessagesMarquee.svelte"
  import TopBar from "../Shared/TopBar.svelte"
  import RoomBar from "./RoomBar.svelte"
  import GameMetaInfo from "../Game/GameMetaInfo.svelte"

  const boardComponentsMap: Record<GameType, typeof SvelteComponent> = {
    Bingo90             : BoardBingo90,
    Bingo75             : BoardBingo75,
    BingoMex            : BoardBingoMex,
    BingoAnimals        : BoardBingoMex,
    BingoCartoon        : BoardBingoMex,
    Movies              : BoardBingoMex,
    MoviesKids          : BoardBingoMex,
    XMas                : BoardBingoMex,
    Football            : BoardBingoMex,
    Halloween           : BoardBingoMex,
    Superheroes         : BoardBingoMex,
    Cartoon             : BoardBingoMex
  }

  const awardScreenShowtime = 1000 * 20 // 20 segundos
  const location = useLocation()

  let game: Game
  let board: Board
  let showMessagesMarquee = true
  let roomEventsChannel: EventsChannel
  let gameEventsChannel: EventsChannel
  let boardEventsChannel: EventsChannel
  let forceShowCards = false
  let showGetBoardForm = false
  let windowWidth = 500
  let awardTimeout: number
  let showAwardScreen = false
  let awardShowConffeti = false
  let awardPlayer: Player
  let figureName: FigureNameType | "bingo"
  let rewardMessage: string

  setContext("getGame", () => game)
  setContext("getBoard", () => board)

  onMount(init)
  onDestroy(() => {
    gameEventsChannel?.destroy()
    boardEventsChannel?.destroy()
    roomEventsChannel?.destroy()
  })

  async function init(): Promise<void> {
    console.log("init PlayRoom");
    
    try {
      roomEventsChannel = $room.getEventsChannel()
      roomEventsChannel.on("figure", onFigure)
      roomEventsChannel.on("bingo", onBingo)

      game = await roomsService.getCurrentGameInRoom($room.id)

      gameEventsChannel = game.getEventsChannel()
      gameEventsChannel.on("add_number", onAddNumber)
      gameEventsChannel.on("finalize", onFinalize)

      //La marquesina de mensajes no debe aparecer
      //si la sala está en modo piscina y el tipo
      //de juego es lotería mexicana o derivado
      showMessagesMarquee = !(
        $room.integratedDisplay.value &&
        [
          "BingoMex",
          "XMas",
          "Football",
          "BingoAnimals",
          "BingoCartoon",
          "Movies",
          "MoviesKids",
          "Halloween",
          "Superheroes",
          "Cartoon"

        ].includes(game.type)
      )

      const query = new URLSearchParams($location.search)

      if (query.has("gameCode")) {
        await createBoard(query.get("gameCode"))
      }

      await getBoard()

      showGetBoardForm = !board
    } catch (error) {
      console.error(error)
    }
  }

  async function getBoard(): Promise<void> {
    boardEventsChannel?.destroy()

    board = await boardsService.getBoard($room.id, game.id)

    if (!board) {
      return
    }

    boardEventsChannel = board.getEventsChannel()
    boardEventsChannel.on("number_checked", onNumberChecked)

    $hasBoard = true
  }

  async function createBoard(gameCode: string): Promise<void> {
    try {
      await boardsService.createBoard($room.id, game.id, gameCode)
    } catch (error) {
      console.error(error)
    }
  }

  function onAddNumber({ data }: RealTimeEvent): void {
    // game.addNumber(data.number)
    game.addNumber(data.game_numbers)
  }

  function onNumberChecked({ data }: RealTimeEvent): void {
    const cell = board.getCell(data.number)
    if (!cell) {
      return
    }
    cell.check()
  }

  function onFigure({ data }: RealTimeEvent): void {
    window.clearTimeout(awardTimeout)
    figureName = data.figure.name
    awardPlayer = playersFactory.createPlayerFromDto(data.player)

    console.log(data);
    
    
    awardShowConffeti = awardPlayer.id === playerService.getPlayer().id
    rewardMessage = $room.roomRewards.figure.text.value
    showAwardScreen = true
    awardTimeout = window.setTimeout(closeAwardScreen, awardScreenShowtime)
  }

  function onBingo({ data }: RealTimeEvent): void {
    window.clearTimeout(awardTimeout)
    figureName = "bingo"
    awardPlayer = playersFactory.createPlayerFromDto(data.player)

    console.log(data);
    awardShowConffeti = awardPlayer.id === playerService.getPlayer().id
    rewardMessage = $room.roomRewards.bingo.text.value
    showAwardScreen = true
    awardTimeout = window.setTimeout(closeAwardScreen, awardScreenShowtime)
  }

  function closeAwardScreen(): void {
    showAwardScreen = false
    figureName = undefined
    awardPlayer = undefined
    rewardMessage = undefined
  }

  function onFinalize(): void {
    closeAwardScreen()
    forceShowCards = true
  }

  async function onBoardCreated(): Promise<void> {
    showGetBoardForm = false
    await getBoard()
  }
</script>

<svelte:window bind:innerWidth={windowWidth} />

<div
  class="playroom-content"
  class:hide-messages-marquee={!showMessagesMarquee}
>
  <TopBar />

  {#if game && ["Bingo90", "Bingo75"].includes(game.type)}
    <RoomBar>
      {#if board}
        <GameMetaInfo {game} />
      {/if}
    </RoomBar>
  {/if}

  {#if game && $room && !showGetBoardForm}
    <If state={$room.integratedDisplay}>
      <div class="historic-container">
        <GameHistoric {game} />
      </div>
    </If>
  {/if}

  <div class="board-container">
    {#if showGetBoardForm}
      <GetBoardForm roomId={$room.id} gameId={game.id} on:submited={onBoardCreated} />
    {/if}

    {#if game && board}
      <svelte:component
        this={boardComponentsMap[game.type]}
        {board}
        {forceShowCards}
        gameType={game.type}
      />
    {/if}
  </div>

  {#if showMessagesMarquee}
    <div class="room-messages">
      <MessagesMarquee roomId={$room.id} />
    </div>
  {/if}

  {#if showAwardScreen}
    <AwardScreen
      {rewardMessage}
      {figureName}
      gameType={game.type}
      showConffeti={awardShowConffeti}
      player={awardPlayer}
    />
  {/if}
</div>

<style lang="scss">
  .playroom-content {
    flex: 1 1 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 1rem;

    &.hide-messages-marquee {
      padding-bottom: 0.5rem;
    }
  }

  .historic-container {
    min-height: 100px;
    max-height: 160px;
    flex: 1 1 0;
  }

  .board-container {
    flex: 2 1 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .room-messages {
    --height: 40px;
    width: 100%;
    height: var(--height);
    position: relative;
    background-color: var(--color-secondary);
  }

  @keyframes win {
    0% {
      transform: translate3d(0, 0, 0);
    }

    50% {
      transform: translate3d(0, 0, var(--grow));
    }

    55% {
      transform: translate3d(0, 0, var(--grow)) rotate(10deg);
    }

    60% {
      transform: translate3d(0, 0, var(--grow)) rotate(-10deg);
    }

    65% {
      transform: translate3d(0, 0, var(--grow)) rotate(10deg);
    }

    70% {
      transform: translate3d(0, 0, var(--grow)) rotate(-10deg);
    }

    75% {
      transform: translate3d(0, 0, var(--grow)) rotate(0deg);
    }

    100% {
      transform: translate3d(0, 0, 0);
    }
  }
</style>
